import { InteractionManager, Platform } from 'react-native';
import { useEffect, useMemo, useRef } from 'react';
import { useKeyEventListener } from 'expo-key-event';

// Cipher labs hardware event codes
const SCAN_START_KEY_EVENT_CODES = new Set(['507', '508']);
const ENTER_KEY_EVENT_CODE = 'Enter';
const VALID_KEY_PREFIX_PATTERN = /^(Digit|Key)/;

const sanitizeKey = (key: string) => {
  return VALID_KEY_PREFIX_PATTERN.test(key)
    ? key.replace(VALID_KEY_PREFIX_PATTERN, '')
    : '';
};

export const useScannerKeyEvents = (submitCode: (code: string) => void) => {
  const codeRef = useRef('');
  const submitCodeRef = useRef(submitCode);

  // submitCode can change based on its dependencies so use a ref to maintain
  // a stable reference to the latest definition instead of from initial mount
  useEffect(() => {
    submitCodeRef.current = submitCode;
  }, [submitCode]);

  const { startListening, stopListening } = useKeyEventListener((event) => {
    const key = event.key;

    if (key === ENTER_KEY_EVENT_CODE && codeRef.current) {
      submitCodeRef.current(codeRef.current);
      codeRef.current = '';
      return;
    }

    if (!SCAN_START_KEY_EVENT_CODES.has(key)) {
      codeRef.current += sanitizeKey(key);
    }
  });

  useEffect(() => {
    InteractionManager.runAfterInteractions(() => {
      // iOS has a cold start issue where listener does not register
      // so reset listening status
      if (Platform.OS === 'ios') {
        stopListening();
      }
      startListening();
    });

    return () => stopListening();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(
    () => ({ startListening, stopListening }),
    [startListening, stopListening],
  );
};
